import React, { useEffect, useMemo, useState } from 'react';
import { HashRouter, Route, Routes } from 'react-router-dom';
import MyPagesMobileMenu from './MyPagesMobileMenu';
import MyPagesDesktopMenu from './MyPagesDesktopMenu';
import MyPagesSettings from './MyPagesSettings';
import MyPagesOrderHistory from './MyPagesOrderHistory';
import MyPagesOrderHistoryOmnium from './MyPagesOrderHistoryOmnium';
import MyPagesOrderDetail from './MyPagesOrderDetail';
import MyPagesUsers from './MyPagesUsers';
import MyPagesAddUser from './MyPagesAddUser';
import MyPagesEditUser from './MyPagesEditUser';
import MyPagesAddresses from './MyPagesAddresses';
import MyPagesAddAddress from './MyPagesAddAddress';
import MyPagesEditAddress from './MyPagesEditAddress';
import { translate } from '../../Services/translation';
import { breakpoint } from '../../styling/mixins/breakpoints';
import styled, { css } from 'styled-components';
import { getColor } from '../../styling/mixins/colors';
import { cells, section } from '../../styling/utils/grid';
import ScrollToTop from './ui/ScrollToTop';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import MyPagesFrontPage from './MyPagesFrontPage';
import MyPagesMyConsents from './MyPagesMyConsents';

const StyledMyPagesContainer = styled.section<{ customProp: boolean }>`
  ${section}
  padding-top: 1.6rem;
  padding-bottom: 2.4rem;
  color: ${getColor('grey', 900)};
  min-height: 72vh;

  ${(props) => css`
    ${props.customProp
      ? css`
          ${breakpoint('xlarge')} {
            display: grid;
            ${cells(4)}
            padding-top: 4rem;
            padding-bottom: 4.8rem;
            gap: 4.8rem;
          }
        `
      : css`
          ${breakpoint('large')} {
            display: grid;
            ${cells(4)}
            padding-top: 4rem;
            padding-bottom: 4.8rem;
            gap: 4.8rem;
          }
        `}
  `};
`;

const navigation = [
  {
    name: translate('mypage.info.title'),
    url: '/',
    isB2bOnly: false,
    hideForCustomerType: ''
  },
  {
    name: translate('mypage.order.title'),
    url: '/orders',
    isB2bOnly: false,
    hideForCustomerType: ''
  },
  {
    name: translate('mypage.frontpage.title'),
    url: '/club',
    isB2bOnly: false,
    hideForCustomerType: ''
  },
  {
    name: translate('mypage.consents.title'),
    url: '/consents',
    isB2bOnly: false,
    hideForCustomerType: 'b2b'
  },
  {
    name: translate('mypage.person.title'),
    url: '/users',
    isB2bOnly: true,
    hideForCustomerType: 'b2c'
  },
  {
    name: translate('mypage.address.addresses'),
    url: '/addresses',
    isB2bOnly: true,
    hideForCustomerType: 'b2c'
  },
];

const MyPagesContainer = (props) => {
  const [
    myPagesStartPageBlockElements,
    setMyPagesStartPageBlockElements,
  ] = useState<any[]>([]);

  useEffect(() => {
    const myPagesStartPageBlocksContainer = document.getElementById(
      'my-pages-startpage-blocks'
    );

    const myPagesStartPageBlockElements = myPagesStartPageBlocksContainer
      ? Array.from(myPagesStartPageBlocksContainer.children)
      : [];
    setMyPagesStartPageBlockElements(myPagesStartPageBlockElements);
  }, []);

  const { current: userInfo } = useTypedSelector(
    (state) => state.userInfo
  );
  const filteredNav = useMemo(
    () =>
      navigation.filter((option) =>
        !userInfo?.isBusinessCustomer ? option.hideForCustomerType !== 'b2c' : option.hideForCustomerType !== 'b2b'
      ),
    [userInfo?.isBusinessCustomer]
  );

  return (
    <StyledMyPagesContainer customProp={props.showTrackingNumber}>
      <HashRouter>
        <ScrollToTop />
        <MyPagesMobileMenu
          navigation={filteredNav}
          showTrackingNumber={props.showTrackingNumber}
        />
        <MyPagesDesktopMenu
          navigation={filteredNav}
          showTrackingNumber={props.showTrackingNumber}
        />
        <Routes>
          <Route
            path="/"
            element={<MyPagesSettings introText={props.myDetailsIntro} />}
          />
          <Route
            path="/orders"
            element={
              <MyPagesOrderHistoryOmnium
                introText={props.ordersIntro}
                showTrackingNumber={props.showTrackingNumber}
              />
            }
          />
          <Route
            path="/club"
            element={
              <MyPagesFrontPage
                blockContent={myPagesStartPageBlockElements}
              />
            }
          />

          {!userInfo?.isBusinessCustomer && (
            <Route
              path="/consents"
              element={<MyPagesMyConsents introText={props.consentsIntro} />}
            />
          )}

          <Route path="/orders/:id" element={<MyPagesOrderDetail />} />

          {userInfo?.isBusinessCustomer && (
            <>
              <Route
                path="/users"
                element={<MyPagesUsers introText={props.personsIntro} />}
              />
              <Route path="/users/add" element={<MyPagesAddUser />} />
              <Route
                path="/users/edit/:id"
                element={<MyPagesEditUser />}
              />
              <Route
                path="/addresses"
                element={
                  <MyPagesAddresses introText={props.addressesIntro} />
                }
              />
              <Route
                path="/addresses/add"
                element={<MyPagesAddAddress />}
              />
              <Route
                path="/addresses/edit/:id"
                element={<MyPagesEditAddress />}
              />
            </>
          )}
        </Routes>
      </HashRouter>
    </StyledMyPagesContainer>
  );
};

export default MyPagesContainer;
